import { memo } from 'react';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useTranslation } from 'react-i18next';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';
import { presenceDotClass, presenceLabel } from '@/lib/presence';
import { CrownIcon } from '@/components/icons';
import { TeamTag } from '@/components/TeamTag';
import type { GuildMemberRow, UserStatus } from '@/types/api';

/**
 * A single roster row in the HeroUI member panel. Memoized: the panel grows
 * its list as it scrolls, and rows already shown must not re-render each time. Pure HeroUI Avatar + a
 * gradient-hue fallback, presence dot and (optional) role colour on the name.
 * The whole row is a {@link UserCardTrigger} so a click opens the shared
 * user hovercard — identical behaviour to the Aether panel, HeroUI styling.
 */
export const HeroMemberRow = memo(function HeroMemberRow(props: {
  member: GuildMemberRow;
  guildId: string;
  ownerId: string;
  status: UserStatus;
  activityLabel?: string;
  activityTone?: 'voice' | 'live';
}) {
  const { t } = useTranslation();
  const m = props.member;
  const label = m.nickname || m.user.displayName || m.user.username;
  const isOffline = props.status === 'OFFLINE';
  const isOwner = m.userId === props.ownerId;

  return (
    <UserCardTrigger
      user={{
        id: m.userId,
        username: m.user.username,
        displayName: m.user.displayName,
        avatarUrl: m.user.avatarUrl,
        status: props.status,
        statusText: m.user.statusText,
        bio: m.user.bio,
      }}
      guildId={props.guildId}
      label={t('ui.member.profileOf', { name: label })}
      className={
        'studio-member flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-[background-color,opacity] duration-150 hover:bg-default/50 ' +
        // Offline members recede, but they must stay READABLE: at opacity 40
        // with a grayscale filter over the whole row, the role colour and the
        // team tag were gone — you couldn't tell an offline moderator from an
        // offline guest without opening their card. The dimming is gentler now
        // and the desaturation is scoped to the avatar (below), so role colour
        // and tag survive; hover still lifts the row to full.
        (isOffline ? 'opacity-70 hover:opacity-100' : '')
      }
    >
      <div className={'relative shrink-0 ' + (isOffline ? 'grayscale' : '')}>
        <IdentityAvatar id={m.userId} name={label} src={m.user.avatarUrl} />
        <span
          title={presenceLabel(props.status)}
          className={
            'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-surface ' +
            presenceDotClass(props.status)
          }
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className="min-w-0 truncate text-sm font-medium text-foreground"
          >
            {label}
          </span>
          {/* Team badge, same as in chat. The roster already carries teamTag —
              it just wasn't rendered here, so the one place you scan to see WHO
              is on the server didn't say which team anyone plays for. */}
          <TeamTag tag={m.user.teamTag} className="shrink-0" link={false} />
        </div>
        {(props.activityLabel || m.user.statusText) && (
          <span
            className={
              'block truncate text-xs ' +
              (props.activityTone === 'live'
                ? 'text-danger'
                : props.activityTone === 'voice'
                  ? 'text-success'
                  : 'text-muted')
            }
          >
            {props.activityLabel || m.user.statusText}
          </span>
        )}
      </div>
      {isOwner && (
        <CrownIcon className="size-3.5 shrink-0 text-warning" aria-label={t('ui.member.owner')} />
      )}
    </UserCardTrigger>
  );
});
